import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { getMemberIdentity, setMemberIdentity } from 'services/setting'

/** A refusal is HTTP 200 with a code; `param` says which field or which rule. */
export interface IdentityRefusal {
    code?: number
    param?: string
    response?: { status?: number }
}

const refusal = (data: { code?: number; params?: unknown } | undefined) =>
    Object.assign(new Error('member identity refused'), {
        code: data?.code,
        param: typeof data?.params === 'string' ? data.params : undefined,
    })

/**
 * Whether this member is still the manager's to correct, and what is on
 * record for them.
 *
 * Asked again on every open and never cached: the answer changes the moment
 * the member signs in, and a form filled from an old answer would offer an
 * edit the backend then refuses.
 */
export function useMemberIdentity(
    moneyPool: string | undefined,
    poolShip: number
) {
    return useQuery(
        ['member-identity', moneyPool, poolShip],
        async () => {
            const data = await getMemberIdentity(moneyPool, poolShip)
            if (data?.code !== 2050 || !data.results) throw refusal(data)
            return data.results
        },
        {
            cacheTime: 0,
            staleTime: 0,
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        }
    )
}

export function useSetMemberIdentity(refetch: () => void) {
    return useMutation({
        mutationFn: async (payload: IMemberIdentityPayload) => {
            const data = await setMemberIdentity(payload)
            if (data?.code !== 2050) throw refusal(data)
            return data
        },
        onSuccess: (data, variables) => {
            // Which fields, never what was typed in them.
            posthog.capture('member_identity_edited', {
                fund_id: variables.moneyPool,
                poolship_id: variables.poolShip,
                changed: Object.keys(variables.changes).sort(),
            })
            refetch()
            toast.success('اطلاعات عضو اصلاح شد')
        },
        onError: (error: IdentityRefusal, variables) => {
            posthog.capture('member_action_failed', {
                action: 'edit_identity',
                fund_id: variables.moneyPool,
                poolship_id: variables.poolShip,
                status: error?.response?.status,
                code: error?.code,
                reason: error?.param,
                changed: Object.keys(variables.changes).sort(),
            })
        },
    })
}
